import { useState, useEffect } from 'react';
import { deviceAlertRuleService } from '../services/deviceAlertRuleService';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';

export const useAlertRules = () => {
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setRules([]);
      setIsLoading(false);
      return;
    }

    const fetchRules = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const fetchedRules = await deviceAlertRuleService.getAlertRules(user.id);
        setRules(fetchedRules);
      } catch (err) {
        console.error('Error fetching alert rules:', err);
        setError(err.message || 'Failed to fetch alert rules');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRules();

    // Set up real-time subscription for alert rules
    const subscription = supabase
      .channel('device_alert_rules')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'device_alert_rules',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchRules();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, isAuthenticated]);

  const createRule = async (ruleData) => {
    if (!user) return null;

    try {
      const createdRule = await deviceAlertRuleService.createAlertRule(user.id, ruleData);
      // Refresh rules list
      const updatedRules = await deviceAlertRuleService.getAlertRules(user.id);
      setRules(updatedRules);
      return createdRule;
    } catch (err) {
      console.error('Error creating alert rule:', err);
      setError(err.message || 'Failed to create alert rule');
      throw err;
    }
  };

  const updateRule = async (ruleId, ruleData) => {
    if (!user) return null;

    try {
      const updatedRule = await deviceAlertRuleService.updateAlertRule(user.id, ruleId, ruleData);
      // Refresh rules list
      const updatedRules = await deviceAlertRuleService.getAlertRules(user.id);
      setRules(updatedRules);
      return updatedRule;
    } catch (err) {
      console.error('Error updating alert rule:', err);
      setError(err.message || 'Failed to update alert rule');
      throw err;
    }
  };

  const deleteRule = async (ruleId) => {
    if (!user) return;

    try {
      await deviceAlertRuleService.deleteAlertRule(user.id, ruleId);
      // Refresh rules list
      const updatedRules = await deviceAlertRuleService.getAlertRules(user.id);
      setRules(updatedRules);
    } catch (err) {
      console.error('Error deleting alert rule:', err);
      setError(err.message || 'Failed to delete alert rule');
      throw err;
    }
  };

  const toggleActive = async (ruleId, isActive) => {
    if (!user) return;

    try {
      // Optimistic update
      setRules(prevRules =>
        prevRules.map(r =>
          r.id === ruleId ? { ...r, isActive } : r
        )
      );

      await deviceAlertRuleService.toggleRuleActive(user.id, ruleId, isActive);
    } catch (err) {
      console.error('Error toggling rule active status:', err);
      // Revert optimistic update
      setRules(prevRules =>
        prevRules.map(r =>
          r.id === ruleId ? { ...r, isActive: !isActive } : r
        )
      );
      setError(err.message || 'Failed to toggle rule status');
      throw err;
    }
  };

  return {
    rules,
    isLoading,
    error,
    createRule,
    updateRule,
    deleteRule,
    toggleActive,
    refetch: async () => {
      if (user) {
        const updatedRules = await deviceAlertRuleService.getAlertRules(user.id);
        setRules(updatedRules);
      }
    }
  };
};

